import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { transfersApi, reportsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Send, 
  Search, 
  Loader2, 
  CheckCircle, 
  Clock, 
  ArrowRight,
  Home,
  MapPin,
  Building2,
  Zap,
  Globe,
  Wallet,
  BadgeCheck,
  FileText,
  Download,
  Printer,
  User,
  DollarSign,
  Calendar,
  Package
} from 'lucide-react';

export default function TrackPage() {
  const [searchParams] = useSearchParams();
  const [trackNumber, setTrackNumber] = useState(searchParams.get('number') || '');
  const [loading, setLoading] = useState(false);
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);
  const [result, setResult] = useState(null);

  const serviceIcons = {
    swift: <Building2 className="w-6 h-6" />,
    moneygram: <Zap className="w-6 h-6" />,
    western_union: <Globe className="w-6 h-6" />,
    wire_transfer: <Send className="w-6 h-6" />,
    ach: <Wallet className="w-6 h-6" />,
    sepa: <BadgeCheck className="w-6 h-6" />,
    instant: <Zap className="w-6 h-6" />,
    crypto_bridge: <DollarSign className="w-6 h-6" />
  };

  const serviceColors = {
    swift: '#3b82f6',
    moneygram: '#ef4444',
    western_union: '#fbbf24',
    wire_transfer: '#8b5cf6',
    ach: '#10b981',
    sepa: '#0ea5e9',
    instant: '#f59e0b',
    crypto_bridge: '#f97316'
  };

  const handleTrack = async () => {
    if (!trackNumber.trim()) {
      toast.error('Please enter a tracking number');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const res = await transfersApi.track(trackNumber.trim());
      setResult(res.data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Transfer not found');
      } else {
        toast.error('Error searching for transfer');
      }
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (searchParams.get('number')) {
      handleTrack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownloadReceipt = async () => {
    if (!result?.tracking_number) return;
    
    setDownloadingReceipt(true);
    try {
      const response = await reportsApi.downloadTransferReceipt(result.tracking_number);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `MoneyMove_Receipt_${result.tracking_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Receipt downloaded successfully');
    } catch (error) {
      toast.error('Failed to download receipt');
    } finally {
      setDownloadingReceipt(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getStageIndex = (stages, currentStage) => {
    return stages.findIndex(s => s.stage === currentStage);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getServiceColor = () => {
    if (!result?.service_type) return '#00ff88';
    return serviceColors[result.service_type] || '#00ff88';
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="track-page">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#00ff88]/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/5 rounded-full blur-[120px]" />
      </div>

      {/* Header */}
      <header className="glass fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <motion.div 
              whileHover={{ scale: 1.05 }}
              className="w-10 h-10 rounded-lg gradient-primary flex items-center justify-center"
            >
              <Send className="w-5 h-5 text-black" />
            </motion.div>
            <span className="text-2xl font-bold text-white">MoneyMove</span>
          </Link>
          
          <Link to="/">
            <Button variant="ghost" className="text-white hover:text-[#00ff88]">
              <Home className="w-5 h-5 mr-2" />
              Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-32 pb-16 px-6 relative z-10">
        <div className="max-w-3xl mx-auto">
          {/* Title */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-10"
          >
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#00ff88]/20 to-[#00ff88]/5 flex items-center justify-center mx-auto mb-6 border border-[#00ff88]/20">
              <Search className="w-10 h-10 text-[#00ff88]" />
            </div>
            <h1 className="text-3xl lg:text-4xl font-bold text-white mb-3">Track Your Transfer</h1>
            <p className="text-gray-400">Enter your tracking number to see real-time status updates</p>
          </motion.div>

          {/* Search Form */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-[#111] to-[#0a0a0a] border border-white/10 rounded-2xl p-6 mb-6"
          >
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-300 flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Tracking Number
                </Label>
                <Input
                  type="text"
                  placeholder="e.g., SWIFT20250101123456ABCD1234"
                  value={trackNumber}
                  onChange={(e) => setTrackNumber(e.target.value.toUpperCase())}
                  onKeyPress={(e) => e.key === 'Enter' && handleTrack()}
                  className="bg-white/5 border-white/10 text-white h-14 text-lg font-mono focus:border-[#00ff88]/50 focus:ring-[#00ff88]/20"
                  data-testid="track-number-input"
                />
              </div>
              
              <Button
                onClick={handleTrack}
                disabled={loading}
                className="w-full h-14 bg-gradient-to-r from-[#00ff88] to-[#00cc6a] text-black font-bold text-lg hover:opacity-90 transition-all"
                data-testid="track-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <>
                    <Search className="w-5 h-5 mr-2" />
                    Track Transfer
                  </>
                )}
              </Button>
            </div>
          </motion.div>

          {/* Result */}
          <AnimatePresence>
            {result && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="bg-gradient-to-br from-[#111] to-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden"
                data-testid="track-result"
              >
                {/* Header with Service Color */}
                <div 
                  className="p-6 border-b border-white/10"
                  style={{ background: `linear-gradient(135deg, ${getServiceColor()}15 0%, transparent 100%)` }}
                >
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                      <div 
                        className="w-14 h-14 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${getServiceColor()}20`, color: getServiceColor() }}
                      >
                        {serviceIcons[result.service_type] || <Send className="w-7 h-7" />}
                      </div>
                      <div>
                        <p className="text-white font-bold text-lg">{result.service_name}</p>
                        <p className="text-gray-400 font-mono text-sm">{result.tracking_number}</p>
                      </div>
                    </div>
                    <motion.span 
                      initial={{ scale: 0.9 }}
                      animate={{ scale: 1 }}
                      className={`px-4 py-2 rounded-full text-sm font-bold ${
                        result.status === 'completed' 
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                          : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                      }`}
                    >
                      {result.status === 'completed' ? '✅ Completed' : '⏳ Processing'}
                    </motion.span>
                  </div>
                </div>

                {/* Amount Display */}
                <div className="p-8 border-b border-white/10 text-center bg-gradient-to-b from-white/[0.02] to-transparent">
                  <p className="text-gray-400 text-sm mb-2 flex items-center justify-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Transfer Amount
                  </p>
                  <motion.p 
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                    className="text-5xl font-bold font-mono"
                    style={{ color: getServiceColor() }}
                  >
                    {result.converted_amount?.toLocaleString() || result.amount?.toLocaleString()} {result.destination_currency}
                  </motion.p>
                  {result.source_currency !== result.destination_currency && (
                    <p className="text-gray-500 mt-2">
                      From {result.amount?.toLocaleString()} {result.source_currency}
                    </p>
                  )}
                </div>

                {/* Transfer Details Grid */}
                <div className="p-6 border-b border-white/10 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white/5 rounded-xl p-4">
                    <p className="text-gray-500 text-xs mb-1 flex items-center gap-1">
                      <User className="w-3 h-3" /> From
                    </p>
                    <p className="text-white font-medium truncate">{result.sender_name}</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4">
                    <p className="text-gray-500 text-xs mb-1 flex items-center gap-1">
                      <User className="w-3 h-3" /> To
                    </p>
                    <p className="text-white font-medium truncate">{result.recipient_name}</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4">
                    <p className="text-gray-500 text-xs mb-1 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> Destination
                    </p>
                    <p className="text-white font-medium">{result.recipient_country}</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4">
                    <p className="text-gray-500 text-xs mb-1 flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> Est. Delivery
                    </p>
                    <p className="text-white font-medium">{result.estimated_delivery}</p>
                  </div>
                </div>

                {/* Timeline */}
                <div className="p-6 border-b border-white/10">
                  <h3 className="text-white font-bold mb-6 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[#00ff88]" />
                    Transfer Progress
                  </h3>
                  
                  <div className="space-y-0 relative">
                    {/* Progress Line */}
                    <div className="absolute left-[7px] top-0 bottom-0 w-0.5 bg-white/10" />
                    
                    {result.all_stages?.map((stage, index) => {
                      const currentIndex = getStageIndex(result.all_stages, result.current_stage);
                      const historyItem = result.tracking_history?.find(h => h.stage === stage.stage);
                      const isCompleted = index <= currentIndex;
                      const isCurrent = index === currentIndex;
                      
                      return (
                        <motion.div 
                          key={stage.stage}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="relative pl-8 pb-6 last:pb-0"
                        >
                          {/* Dot */}
                          <div 
                            className={`absolute left-0 top-0 w-4 h-4 rounded-full border-2 ${
                              isCompleted 
                                ? 'bg-[#00ff88] border-[#00ff88]' 
                                : 'bg-[#0a0a0a] border-white/20'
                            } ${isCurrent ? 'ring-4 ring-[#00ff88]/20' : ''}`}
                          />
                          
                          {/* Completed line segment */}
                          {isCompleted && index < currentIndex && (
                            <div 
                              className="absolute left-[7px] top-4 w-0.5 bg-[#00ff88]"
                              style={{ height: 'calc(100% - 16px)' }}
                            />
                          )}
                          
                          <div className={`${isCompleted ? 'opacity-100' : 'opacity-50'}`}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xl">{stage.icon}</span>
                              <p className={`font-medium ${isCompleted ? 'text-white' : 'text-gray-500'}`}>
                                {stage.name}
                              </p>
                              {isCompleted && index <= currentIndex && (
                                <CheckCircle className="w-4 h-4 text-[#00ff88]" />
                              )}
                            </div>
                            <p className={`text-sm ${isCompleted ? 'text-gray-400' : 'text-gray-600'}`}>
                              {stage.description}
                            </p>
                            {historyItem && (
                              <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {formatDate(historyItem.timestamp)}
                                </span>
                                {historyItem.location && (
                                  <span className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    {historyItem.location}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="p-6 flex flex-wrap gap-3">
                  <Button
                    onClick={handleDownloadReceipt}
                    disabled={downloadingReceipt}
                    className="flex-1 h-12 bg-gradient-to-r from-[#00ff88] to-[#00cc6a] text-black font-bold hover:opacity-90"
                    data-testid="download-receipt-btn"
                  >
                    {downloadingReceipt ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Download className="w-5 h-5 mr-2" />
                        Download Receipt (PDF)
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={handlePrint}
                    variant="outline"
                    className="h-12 border-white/20 text-white hover:bg-white/10"
                  >
                    <Printer className="w-5 h-5 mr-2" />
                    Print
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Help Text */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-center mt-8"
          >
            <p className="text-gray-500 text-sm">
              Tracking numbers start with the service code (SWIFT, WU, MONEYGRAM, WIRE, ACH, SEPA, INSTANT, CRYPTO)
            </p>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
